import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'

import { useTableColumnPrefs, type TableColumnDef } from '../hooks/useTableColumnPrefs'

import { previewCatalogMatch } from '../api/catalogImport'
import { matchDeterminationLabel, type MatchCandidateDto } from '../api/catalogImportTypes'
import { listProductTypes, mergeProductTypes, resolveProductTypes, type ProductTypeDto } from '../api/productTypes'
import {
  assignCatalogProductType,
  backfillCatalogBrands,
  bulkDeleteCatalogItems,
  confirmCatalogSuggestedMatch,
  createCatalogItem,
  deleteCatalogItem,
  dismissCatalogDuplicates,
  getCatalogSummary,
  listCatalogDuplicates,
  listSupplierCatalogItems,
  listSupplierCatalogFilterOptions,
  normalizeCatalogBrands,
  rematchSupplierCatalog,
  updateCatalogItemFields,
  type CatalogDuplicateGroupDto,
  type CatalogItemsPageDto,
  type CatalogSummaryDto,
  type SupplierCatalogItemDto,
} from '../api/supplierCatalog'
import {
  bulkDeleteRestaurantMyPricesItems,
  confirmRestaurantMyPricesSuggestion,
  deleteRestaurantMyPricesItem,
  dismissMyPricesDuplicates,
  getRestaurantMyPricesSummary,
  listMyPricesDuplicates,
  listRestaurantMyPricesFilterOptions,
  listRestaurantMyPricesItems,
  listRestaurantPrivateSuppliers,
  type RestaurantPrivateSupplierDto,
} from '../api/restaurantMyPrices'
import { ProductTypeCascadePicker } from './ProductTypeCascadePicker'
import { CatalogItemEditPanel, type CatalogItemEditMode } from './CatalogItemEditPanel'
import { CatalogDuplicateMergePanel } from './CatalogDuplicateMergePanel'
import { CatalogVersionHistoryPanel } from './CatalogVersionHistoryPanel'
import { IconCheck, IconPencil, IconTrash } from './icons/CatalogActionIcons'
import { SuggestTextField } from './SuggestTextField'
import { MarketEmptyState } from './MarketEmptyState'
import { MarketPageHeader } from './MarketPageHeader'
import { PageScrollJumpButtons, TableScrollJump } from './PageScrollJumpButtons'
import { DataTable } from './DataTable'
import { ResizableTh } from './ResizableTh'
import { SelectCombobox } from './SelectCombobox'
import { StatTile, StatTileRow } from './StatTile'
import { TableColumnSettings } from './TableColumnSettings'
import { formatProductTypeLabel, expandProductTypeToDescendantIds } from '../screens/catalogFilters'
import { formatAnalyticsPct, priceMoveToneClass } from '../screens/marketAnalyticsFormat'
import { formatUnitLabel } from '../utils/formatUnitLabel'
import { cartLineKey } from '../cart/cartLineKey'
import { useCart } from '../cart/CartProvider'
import { useToast } from './ToastProvider'
import { isPivotModeEnabled } from '../pivot/pivotMode'

const formatCatalogPrice = (item: SupplierCatalogItemDto): string => {
  if (item.price != null) {
    return String(item.price)
  }
  const unit = item.unit?.trim()
  if (unit && /^\d+([.,]\d+)?$/.test(unit)) {
    return unit
  }
  return '—'
}

const formatCatalogPriceRub = (price: number | null): string => {
  if (price == null || Number.isNaN(price)) {
    return '—'
  }
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    maximumFractionDigits: 2,
  }).format(price)
}

const formatUpdatedAt = (value: string | null): string => {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

const freshnessHint = (
  item: SupplierCatalogItemDto,
  /** Supplier admin sees delist status; restaurant never does — hidden rows simply stay out of search. */
  showDelistedHint: boolean,
): string | null => {
  if (showDelistedHint && item.buyerSearchVisible === false) {
    return 'Скрыта из выдачи покупателям'
  }
  if (item.freshnessStatus == null || item.freshnessStatus === 'fresh') return null
  if (item.daysSinceUpdate == null) return null
  return `${item.daysSinceUpdate} дн. без обновления`
}

const SUPPLIER_CATALOG_COLUMNS: TableColumnDef[] = [
  {
    id: 'select',
    label: 'Выбор',
    required: true,
    pin: 'start',
    hideInSettings: true,
    defaultWidth: 48,
    minWidth: 40,
  },
  { id: 'name', label: 'Позиция', required: true, defaultWidth: 240, minWidth: 120 },
  { id: 'sku', label: 'Артикул', defaultVisible: false, defaultWidth: 120, minWidth: 72 },
  { id: 'price', label: 'Цена', defaultWidth: 200, minWidth: 140 },
  { id: 'unit', label: 'Ед. изм.', defaultWidth: 88, minWidth: 64 },
  { id: 'package', label: 'Фасовка', defaultVisible: false, defaultWidth: 110, minWidth: 72 },
  {
    id: 'minShipment',
    label: 'Мин. отгрузка',
    defaultVisible: false,
    defaultWidth: 110,
    minWidth: 72,
  },
  { id: 'brand', label: 'Бренд', defaultWidth: 120, minWidth: 72 },
  { id: 'country', label: 'Страна', defaultWidth: 110, minWidth: 72 },
  { id: 'category', label: 'Категория', defaultWidth: 160, minWidth: 96 },
  { id: 'updated', label: 'Дата обновления', defaultWidth: 140, minWidth: 96 },
  { id: 'match', label: 'Тип определения', defaultWidth: 140, minWidth: 96 },
  {
    id: 'actions',
    label: 'Действия',
    required: true,
    pin: 'end',
    hideInSettings: true,
    defaultWidth: 120,
    minWidth: 96,
  },
]

const MY_PRICES_SUPPLIER_COLUMN: TableColumnDef = {
  id: 'supplier',
  label: 'Поставщик',
  defaultWidth: 180,
  minWidth: 96,
}

export type SupplierCatalogPageProps = {
  organizationId: number
  importPath: string
  catalogMode?: CatalogItemEditMode
  title?: string
  description?: string
  backLink?: ReactNode
  /** Тихий caption под метриками (лимиты тарифа и т.п.). */
  billingNote?: string | null
  /** Operator-only: full catalog version history + restore. */
  showCatalogVersions?: boolean
}

export const SupplierCatalogPage = ({
  organizationId,
  importPath,
  catalogMode = 'supplier',
  title = 'Каталог',
  description,
  backLink,
  showCatalogVersions = false,
  billingNote = null,
}: SupplierCatalogPageProps) => {
  const isRestaurantMyPrices = catalogMode === 'restaurant-my-prices'
  const headerDescription =
    description !== undefined
      ? description
      : isRestaurantMyPrices
        ? undefined
        : 'Позиции прайс-листа и сопоставление с категориями Провизиум.'
  /* Bump suffix when default columns change so stale localStorage prefs drop. */
  const tableId = isRestaurantMyPrices ? 'buyer:my-prices:v3' : 'supplier:catalog'
  const catalogColumns = useMemo(() => {
    if (!isRestaurantMyPrices) {
      return SUPPLIER_CATALOG_COLUMNS
    }
    const hiddenByDefault = new Set(['brand', 'country', 'match'])
    const base = SUPPLIER_CATALOG_COLUMNS.map((column) =>
      hiddenByDefault.has(column.id) ? { ...column, defaultVisible: false } : column,
    )
    const nameIndex = base.findIndex((column) => column.id === 'name')
    if (nameIndex < 0) {
      return [...base, MY_PRICES_SUPPLIER_COLUMN]
    }
    return [...base.slice(0, nameIndex + 1), MY_PRICES_SUPPLIER_COLUMN, ...base.slice(nameIndex + 1)]
  }, [isRestaurantMyPrices])
  const columnPrefs = useTableColumnPrefs(tableId, catalogColumns)
  const navigate = useNavigate()
  const { addOffer, hasLine } = useCart()
  const { showToast } = useToast()
  const requestLabel = isPivotModeEnabled() ? 'заявку' : 'корзину'
  const [summary, setSummary] = useState<CatalogSummaryDto | null>(null)
  const [page, setPage] = useState<CatalogItemsPageDto | null>(null)
  const [pageIndex, setPageIndex] = useState(0)
  const [productTypes, setProductTypes] = useState<ProductTypeDto[]>([])
  const [brandOptions, setBrandOptions] = useState<string[]>([])
  const [countryOptions, setCountryOptions] = useState<string[]>([])
  const [matchStatus, setMatchStatus] = useState('')
  const [productTypeFilter, setProductTypeFilter] = useState<number | null>(null)
  const [unassignedOnly, setUnassignedOnly] = useState(false)
  const [privateSupplierId, setPrivateSupplierId] = useState<number | null>(null)
  const [privateSuppliers, setPrivateSuppliers] = useState<RestaurantPrivateSupplierDto[]>([])
  const [searchDraft, setSearchDraft] = useState('')
  const [search, setSearch] = useState('')
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [newItemName, setNewItemName] = useState('')
  const [newItemBrand, setNewItemBrand] = useState('')
  const [newItemSku, setNewItemSku] = useState('')
  const [newItemCountry, setNewItemCountry] = useState('')
  const [newItemPrice, setNewItemPrice] = useState('')
  const [newItemUnit, setNewItemUnit] = useState('')
  const [newItemMinShipmentQty, setNewItemMinShipmentQty] = useState('')
  const [newItemPackageSize, setNewItemPackageSize] = useState('')
  const [newItemProductTypeId, setNewItemProductTypeId] = useState<number | null>(null)
  const [createCandidates, setCreateCandidates] = useState<MatchCandidateDto[]>([])
  const [loadingCreateSuggestions, setLoadingCreateSuggestions] = useState(false)
  const [creatingItem, setCreatingItem] = useState(false)
  const [expandedId, setExpandedId] = useState<number | null>(null)
  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const [loading, setLoading] = useState(true)
  const [rematching, setRematching] = useState(false)
  const [tidyingBrands, setTidyingBrands] = useState(false)
  const [rematchMessage, setRematchMessage] = useState<string | null>(null)
  const [duplicateGroups, setDuplicateGroups] = useState<CatalogDuplicateGroupDto[]>([])
  const [showDuplicates, setShowDuplicates] = useState(true)
  const [confirmingId, setConfirmingId] = useState<number | null>(null)
  const [deletingId, setDeletingId] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [showMoreFilters, setShowMoreFilters] = useState(false)
  // Держим свежий productTypes в ref, чтобы async-загрузка читала его без добавления
  // в зависимости колбэка. Синхронизируем в эффекте (после рендера), а не во время рендера.
  const productTypesRef = useRef(productTypes)
  useEffect(() => {
    productTypesRef.current = productTypes
  }, [productTypes])

  const typeNames = new Map(productTypes.map((type) => [type.id, type.name]))

  const load = useCallback(
    async (
      index: number,
      statusFilter: string,
      typeFilter: number | null,
      onlyUnassigned: boolean,
      searchQuery: string,
      supplierFilterId: number | null,
    ) => {
      if (!Number.isFinite(organizationId) || organizationId <= 0) {
        return
      }
      setLoading(true)
      setError(null)
      try {
        const [summaryData, pageData] = await Promise.all([
          isRestaurantMyPrices
            ? getRestaurantMyPricesSummary(organizationId)
            : getCatalogSummary(organizationId),
          isRestaurantMyPrices
            ? listRestaurantMyPricesItems({
                organizationId,
                page: index,
                matchStatus: statusFilter || undefined,
                productTypeId: typeFilter ?? undefined,
                productTypeIds:
                  typeFilter != null
                    ? expandProductTypeToDescendantIds(typeFilter, productTypesRef.current)
                    : undefined,
                unassignedProductType: onlyUnassigned || undefined,
                search: searchQuery || undefined,
                privateSupplierId: supplierFilterId,
              })
            : listSupplierCatalogItems({
                organizationId,
                page: index,
                matchStatus: statusFilter || undefined,
                productTypeId: typeFilter ?? undefined,
                unassignedProductType: onlyUnassigned || undefined,
                search: searchQuery || undefined,
              }),
        ])
        setSummary(summaryData)
        setPage(pageData)
        setPageIndex(index)
        setSelectedIds([])
        try {
          const duplicates = isRestaurantMyPrices
            ? await listMyPricesDuplicates(organizationId)
            : await listCatalogDuplicates(organizationId)
          // Бэкенд уже отфильтровал группы, разведённые решением организации.
          setDuplicateGroups(duplicates.groups)
          setShowDuplicates(duplicates.groups.length > 0)
        } catch {
          setDuplicateGroups([])
        }
      } catch (loadError) {
        setError(loadError instanceof Error ? loadError.message : 'Не удалось загрузить каталог')
      } finally {
        setLoading(false)
      }
    },
    [organizationId, isRestaurantMyPrices],
  )

  useEffect(() => {
    void listProductTypes().then(setProductTypes).catch(() => undefined)
  }, [])

  useEffect(() => {
    if (!page?.items.length) {
      return
    }
    const knownIds = new Set(productTypes.map((type) => type.id))
    const missingIds = [
      ...new Set(
        page.items
          .map((item) => item.productTypeId)
          .filter((id): id is number => id != null && !knownIds.has(id)),
      ),
    ]
    if (missingIds.length === 0) {
      return
    }
    void resolveProductTypes(missingIds)
      .then((resolved) => {
        setProductTypes((current) => mergeProductTypes(current, resolved))
      })
      .catch(() => undefined)
  }, [page, productTypes])

  const loadFilterOptions = useCallback(() => {
    if (!Number.isFinite(organizationId) || organizationId <= 0) {
      return
    }
    void (isRestaurantMyPrices
      ? listRestaurantMyPricesFilterOptions(organizationId)
      : listSupplierCatalogFilterOptions(organizationId))
      .then((options) => {
        setBrandOptions(options.brands)
        setCountryOptions(options.countries)
      })
      .catch(() => undefined)
  }, [organizationId, isRestaurantMyPrices])

  useEffect(() => {
    loadFilterOptions()
  }, [loadFilterOptions])

  useEffect(() => {
    if (!isRestaurantMyPrices || !Number.isFinite(organizationId) || organizationId <= 0) {
      setPrivateSuppliers([])
      return
    }
    void listRestaurantPrivateSuppliers(organizationId)
      .then(setPrivateSuppliers)
      .catch(() => setPrivateSuppliers([]))
  }, [organizationId, isRestaurantMyPrices])

  useEffect(() => {
    void load(0, matchStatus, productTypeFilter, unassignedOnly, search, privateSupplierId)
  }, [load, matchStatus, productTypeFilter, unassignedOnly, search, privateSupplierId])

  useEffect(() => {
    const name = newItemName.trim()
    if (!showCreateForm || !name) {
      setCreateCandidates([])
      return
    }
    setLoadingCreateSuggestions(true)
    const timer = window.setTimeout(() => {
      void previewCatalogMatch(organizationId, name, newItemBrand.trim() || null)
        .then((preview) => setCreateCandidates(preview.candidates))
        .catch(() => setCreateCandidates([]))
        .finally(() => setLoadingCreateSuggestions(false))
    }, 300)
    return () => window.clearTimeout(timer)
  }, [organizationId, newItemBrand, newItemName, showCreateForm])

  const resetCreateForm = () => {
    setNewItemName('')
    setNewItemBrand('')
    setNewItemSku('')
    setNewItemCountry('')
    setNewItemPrice('')
    setNewItemUnit('')
    setNewItemMinShipmentQty('')
    setNewItemPackageSize('')
    setNewItemProductTypeId(null)
    setCreateCandidates([])
  }

  const handleSearch = () => {
    setSearch(searchDraft.trim())
  }

  const handleResetSearch = () => {
    setSearchDraft('')
    setSearch('')
    setMatchStatus('')
    setProductTypeFilter(null)
    setUnassignedOnly(false)
    setPrivateSupplierId(null)
  }

  const toggleSelected = (itemId: number) => {
    setSelectedIds((current) =>
      current.includes(itemId) ? current.filter((id) => id !== itemId) : [...current, itemId],
    )
  }

  const cartKeyForItem = (item: SupplierCatalogItemDto) =>
    cartLineKey({
      supplierKind: 'external',
      supplierOrganizationId: null,
      privateSupplierId: item.privateSupplierId,
      catalogItemId: item.id,
    })

  const handleBulkAddToCart = () => {
    if (!isRestaurantMyPrices || !page) {
      return
    }
    const selected = page.items.filter((item) => selectedIds.includes(item.id))
    let added = 0
    let skippedNoType = 0
    let alreadyInCart = 0
    for (const item of selected) {
      if (item.productTypeId == null) {
        skippedNoType += 1
        continue
      }
      if (hasLine(cartKeyForItem(item))) {
        alreadyInCart += 1
        continue
      }
      addOffer({
        offer: {
          itemId: item.id,
          supplierOrganizationId: null,
          supplierOrganizationName: item.privateSupplierName ?? 'Поставщик',
          privateSupplierId: item.privateSupplierId,
          legalName: null,
          inn: null,
          isRegisteredSupplier: false,
          itemName: item.supplierName,
          brand: item.brand,
          country: item.country,
          unit: item.unit,
          price: item.price != null ? String(item.price) : null,
          minShipmentQty: item.minShipmentQty != null ? String(item.minShipmentQty) : null,
          packageSize: item.packageSize,
          productTypeId: item.productTypeId,
          productTypeCode: item.productTypeCode,
          productTypeName: item.productTypeName,
          productTypeActive: item.productTypeActive,
          supplierResponseScore: null,
          rankingPrice: null,
          matchedTags: [],
        },
        productTypeName: formatProductTypeLabel(
          item.productTypeId,
          item.productTypeName,
          item.productTypeActive,
          new Map(),
        ),
      })
      added += 1
    }
    if (added === 0 && skippedNoType === 0) {
      showToast(alreadyInCart > 0 ? 'Выбранные позиции уже в заявке' : 'Нечего добавлять')
      return
    }
    showToast(
      skippedNoType > 0
        ? `Добавлено: ${added}. Без категории пропущено: ${skippedNoType}`
        : `Добавлено в ${requestLabel}: ${added}`,
    )
  }

  const handleBulkEdit = () => {
    if (selectedIds.length !== 1) {
      showToast('Выберите одну позицию для редактирования')
      return
    }
    setExpandedId(selectedIds[0] ?? null)
  }

  const handleItemSaved = (updated: SupplierCatalogItemDto) => {
    setPage((current) =>
      current
        ? {
            ...current,
            items: current.items.map((item) => (item.id === updated.id ? updated : item)),
          }
        : current,
    )
    void load(pageIndex, matchStatus, productTypeFilter, unassignedOnly, search, privateSupplierId)
  }

  const handleItemDeleted = () => {
    setExpandedId(null)
    void load(pageIndex, matchStatus, productTypeFilter, unassignedOnly, search, privateSupplierId)
  }

  const handleConfirmMatch = async (item: SupplierCatalogItemDto) => {
    const status = item.matchStatus.toLowerCase()
    if (status !== 'suggested' && status !== 'auto') {
      return
    }
    if (item.productTypeId == null) {
      return
    }
    setConfirmingId(item.id)
    setError(null)
    try {
      const updated = isRestaurantMyPrices
        ? await confirmRestaurantMyPricesSuggestion(item.id)
        : await confirmCatalogSuggestedMatch(item.id)
      handleItemSaved(updated)
      const nextSummary = isRestaurantMyPrices
        ? await getRestaurantMyPricesSummary(organizationId)
        : await getCatalogSummary(organizationId)
      setSummary(nextSummary)
    } catch (confirmError) {
      setError(confirmError instanceof Error ? confirmError.message : 'Не удалось подтвердить категорию')
    } finally {
      setConfirmingId(null)
    }
  }

  const handleQuickDelete = async (item: SupplierCatalogItemDto) => {
    if (!window.confirm(`Удалить позицию «${item.supplierName}»?`)) {
      return
    }
    setDeletingId(item.id)
    setError(null)
    try {
      if (isRestaurantMyPrices) {
        await deleteRestaurantMyPricesItem(item.id)
      } else {
        await deleteCatalogItem(item.id)
      }
      if (expandedId === item.id) {
        setExpandedId(null)
      }
      setSelectedIds((current) => current.filter((id) => id !== item.id))
      handleItemDeleted()
    } catch (deleteError) {
      setError(deleteError instanceof Error ? deleteError.message : 'Не удалось удалить позицию')
    } finally {
      setDeletingId(null)
    }
  }

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) {
      return
    }
    if (!window.confirm(`Удалить выбранные позиции (${selectedIds.length})?`)) {
      return
    }
    try {
      if (isRestaurantMyPrices) {
        await bulkDeleteRestaurantMyPricesItems({
          organizationId,
          itemIds: selectedIds,
        })
      } else {
        await bulkDeleteCatalogItems({
          organizationId,
          itemIds: selectedIds,
        })
      }
      void load(pageIndex, matchStatus, productTypeFilter, unassignedOnly, search, privateSupplierId)
    } catch (deleteError) {
      setError(deleteError instanceof Error ? deleteError.message : 'Не удалось удалить позиции')
    }
  }

  const handleRematch = async () => {
    if (
      !window.confirm(
        'Пересчитать матчинг для всех активных позиций? Ручные правки категорий и брендов будут сохранены.',
      )
    ) {
      return
    }
    setRematching(true)
    setError(null)
    setRematchMessage(null)
    try {
      const result = await rematchSupplierCatalog(organizationId)
      setRematchMessage(
        `Пересчитано: ${result.rematchedCount}, пропущено вручную: ${result.skippedManualCount}`,
      )
      void load(pageIndex, matchStatus, productTypeFilter, unassignedOnly, search, privateSupplierId)
    } catch (rematchError) {
      setError(rematchError instanceof Error ? rematchError.message : 'Не удалось пересчитать матчинг')
    } finally {
      setRematching(false)
    }
  }

  // Дозаполнение и сведение — два прохода по разным позициям (пустой бренд против уже
  // проставленного), но оператору полезны только вместе и строго в этом порядке: иначе
  // только что дозаполненные марки останутся с разнобоем в написании.
  const handleTidyBrands = async () => {
    if (
      !window.confirm(
        'Привести бренды в порядок? У позиций без бренда марка определится по названию, разные написания одной марки объединятся, значения, которые брендом не являются, будут очищены. Ручные правки сохранятся, категории не пересчитываются.',
      )
    ) {
      return
    }
    setTidyingBrands(true)
    setError(null)
    setRematchMessage(null)
    try {
      const backfill = await backfillCatalogBrands(organizationId)
      const normalize = await normalizeCatalogBrands(organizationId)
      setRematchMessage(
        `Дозаполнено пустых: ${backfill.filled}, объединено написаний: ${normalize.canonicalized}, ` +
          `очищено не-брендов: ${normalize.cleared}, защищено от перезаписи прайсом: ${normalize.protectedFromReimport}`,
      )
      loadFilterOptions()
      void load(pageIndex, matchStatus, productTypeFilter, unassignedOnly, search, privateSupplierId)
    } catch (tidyError) {
      setError(tidyError instanceof Error ? tidyError.message : 'Не удалось привести бренды в порядок')
    } finally {
      setTidyingBrands(false)
    }
  }

  const handleCreateItem = async () => {
    if (!newItemName.trim()) {
      return
    }
    setCreatingItem(true)
    setError(null)
    try {
      const created = await createCatalogItem({
        organizationId,
        name: newItemName,
        brand: newItemBrand || null,
        externalSku: newItemSku || null,
      })

      const priceValue = newItemPrice.trim() ? Number(newItemPrice.replace(',', '.')) : null
      const minShipmentValue = newItemMinShipmentQty.trim()
        ? Number(newItemMinShipmentQty.replace(',', '.'))
        : null
      const country = newItemCountry.trim() || null
      const unit = newItemUnit.trim() || null
      const packageSize = newItemPackageSize.trim() || null

      if (
        country != null ||
        priceValue != null ||
        unit != null ||
        minShipmentValue != null ||
        packageSize != null
      ) {
        await updateCatalogItemFields(created.id, {
          country,
          price: priceValue,
          unit,
          minShipmentQty: minShipmentValue,
          packageSize,
        })
      }

      if (newItemProductTypeId != null) {
        await assignCatalogProductType(created.id, newItemProductTypeId)
      }

      resetCreateForm()
      setShowCreateForm(false)
      void load(pageIndex, matchStatus, productTypeFilter, unassignedOnly, search, privateSupplierId)
    } catch (createError) {
      setError(createError instanceof Error ? createError.message : 'Не удалось добавить позицию')
    } finally {
      setCreatingItem(false)
    }
  }

  return (
    <section
      className={
        isRestaurantMyPrices
          ? 'market-page market-page--wide market-page--my-prices'
          : 'market-page market-page--wide'
      }
    >
      <div className="market-page__intro">
        {backLink ? <nav className="market-page__breadcrumb">{backLink}</nav> : null}

        <MarketPageHeader
          title={title}
          description={headerDescription}
          actions={
            <>
              <button
                type="button"
                className="echat-btn echat-btn--primary"
                onClick={() => navigate(importPath)}
              >
                Импорт прайса
              </button>
              {!isRestaurantMyPrices ? (
                <>
                  <button
                    type="button"
                    className="echat-btn echat-btn--ghost"
                    onClick={() => setShowCreateForm((current) => !current)}
                  >
                    {showCreateForm ? 'Скрыть форму' : 'Добавить позицию'}
                  </button>
                  <button
                    type="button"
                    className="echat-btn echat-btn--ghost"
                    disabled={tidyingBrands || rematching || loading}
                    onClick={() => void handleTidyBrands()}
                  >
                    {tidyingBrands ? 'Разбор брендов…' : 'Привести бренды в порядок'}
                  </button>
                  <button
                    type="button"
                    className="echat-btn echat-btn--ghost"
                    disabled={rematching || tidyingBrands || loading}
                    onClick={() => void handleRematch()}
                  >
                    {rematching ? 'Пересчёт…' : 'Пересчитать всё'}
                  </button>
                </>
              ) : null}
            </>
          }
        />

        {showCatalogVersions ? (
          <CatalogVersionHistoryPanel
            organizationId={organizationId}
            mode={isRestaurantMyPrices ? 'buyer-private-supplier' : 'supplier'}
            privateSupplierId={privateSupplierId}
            onRestored={() =>
              void load(pageIndex, matchStatus, productTypeFilter, unassignedOnly, search, privateSupplierId)
            }
          />
        ) : null}

        {isRestaurantMyPrices ? (
          <div className="market-my-prices-intro">
            {summary && summary.needsAttention > 0 ? (
              <section className="market-overview-priority" aria-label="Требует внимания">
                <div className="market-overview-priority__inner">
                  <div className="market-overview-priority__content">
                    <h2 className="market-overview-priority__title">Категории требуют проверки</h2>
                    <p className="market-overview-priority__text">
                      {summary.needsAttention === 1
                        ? '1 позиция без категории или требует проверки типа.'
                        : `${summary.needsAttention} поз. без категории или требуют проверки типа.`}
                    </p>
                  </div>
                </div>
              </section>
            ) : null}

            {summary ? (
              <StatTileRow className="market-my-prices-metrics">
                <StatTile label="Всего позиций" value={summary.totalItems} />
                <StatTile label="Требуют внимания" value={summary.needsAttention} />
                <StatTile label="Определены автоматически" value={summary.matchAuto} />
                <StatTile label="Заданы вручную" value={summary.matchManual} />
                <StatTile label="Без типа" value={summary.matchUnmatched} />
              </StatTileRow>
            ) : null}

            {billingNote ? <p className="market-my-prices-billing">{billingNote}</p> : null}

            {duplicateGroups.length > 0 && !showDuplicates ? (
              <button
                type="button"
                className="echat-btn echat-btn--ghost market-my-prices-duplicates-toggle"
                onClick={() => setShowDuplicates(true)}
              >
                Показать дубликаты ({duplicateGroups.length})
              </button>
            ) : null}
          </div>
        ) : (
          <div
            className={`market-catalog-intro-split${
              summary ? '' : ' market-catalog-intro-split--hint-only'
            }`}
          >
            <p className="market-hint market-hint--in-split">
              Загрузите прайс через импорт — система сопоставит позиции с категориями Провизиум. Проверьте
              строки, которые требуют внимания или без типа, и при необходимости назначьте категорию вручную.
            </p>

            {summary ? (
              <article className="echat-panel market-catalog-summary market-catalog-summary--inline">
                <p className="echat-panel__text market-catalog-summary__headline">
                  <span>Всего позиций: {summary.totalItems}</span>
                  {summary.needsAttention > 0 ? (
                    <span className="market-catalog-summary__attention">
                      Требуют внимания: {summary.needsAttention}
                    </span>
                  ) : null}
                </p>
                <p className="echat-muted">
                  Определены автоматически: {summary.matchAuto} · Требуют внимания:{' '}
                  {summary.matchSuggested} · Заданы вручную: {summary.matchManual} · Без типа:{' '}
                  {summary.matchUnmatched}
                </p>
                {!isRestaurantMyPrices && (summary.staleItems > 0 || summary.hiddenFromSearch > 0) ? (
                  <p className="market-catalog-summary__freshness">
                    {summary.staleItems > 0
                      ? `Без обновления более 7 дн.: ${summary.staleItems}`
                      : null}
                    {summary.staleItems > 0 && summary.hiddenFromSearch > 0 ? ' · ' : null}
                    {summary.hiddenFromSearch > 0
                      ? `Скрыто из выдачи: ${summary.hiddenFromSearch}`
                      : null}
                  </p>
                ) : null}
                {duplicateGroups.length > 0 && !showDuplicates ? (
                  <button
                    type="button"
                    className="echat-btn echat-btn--ghost market-catalog-summary__duplicates-toggle"
                    onClick={() => setShowDuplicates(true)}
                  >
                    Показать дубликаты ({duplicateGroups.length})
                  </button>
                ) : null}
              </article>
            ) : null}
          </div>
        )}
      </div>

      {showDuplicates && duplicateGroups.length > 0 ? (
        <CatalogDuplicateMergePanel
          organizationId={organizationId}
          groups={duplicateGroups}
          catalogMode={catalogMode}
          onDismiss={() => setShowDuplicates(false)}
          onNotDuplicates={(group) => {
            void (async () => {
              const itemIds = group.items.map((item) => item.id)
              try {
                if (isRestaurantMyPrices) {
                  await dismissMyPricesDuplicates({ organizationId, itemIds })
                } else {
                  await dismissCatalogDuplicates({ organizationId, itemIds })
                }
                const rest = duplicateGroups.filter((item) => item !== group)
                setDuplicateGroups(rest)
                if (rest.length === 0) {
                  setShowDuplicates(false)
                }
              } catch (dismissError) {
                setError(
                  dismissError instanceof Error
                    ? dismissError.message
                    : 'Не удалось сохранить решение «это не дубликаты»',
                )
              }
            })()
          }}
          onMerged={async () => {
            await load(pageIndex, matchStatus, productTypeFilter, unassignedOnly, search, privateSupplierId)
          }}
        />
      ) : null}

      {!isRestaurantMyPrices && showCreateForm ? (
        <article className="echat-panel market-catalog-create">
          <h2 className="echat-panel__subtitle">Новая позиция</h2>
          <div className="market-catalog-edit__grid">
            <label className="echat-field">
              <span className="echat-field__label">Наименование</span>
              <input
                className="echat-field__input"
                value={newItemName}
                required
                onChange={(event) => setNewItemName(event.target.value)}
              />
            </label>
            <label className="echat-field">
              <span className="echat-field__label">Артикул</span>
              <input
                className="echat-field__input"
                value={newItemSku}
                onChange={(event) => setNewItemSku(event.target.value)}
              />
            </label>
            <label className="echat-field">
              <span className="echat-field__label">Цена</span>
              <input
                className="echat-field__input"
                inputMode="decimal"
                value={newItemPrice}
                onChange={(event) => setNewItemPrice(event.target.value)}
              />
            </label>
            <label className="echat-field">
              <span className="echat-field__label">Ед. изм.</span>
              <input
                className="echat-field__input"
                value={newItemUnit}
                onChange={(event) => setNewItemUnit(event.target.value)}
              />
            </label>
            <SuggestTextField
              label="Бренд"
              value={newItemBrand}
              suggestions={brandOptions}
              listId="catalog-create-brand"
              onChange={setNewItemBrand}
            />
            <SuggestTextField
              label="Страна"
              value={newItemCountry}
              suggestions={countryOptions}
              listId="catalog-create-country"
              onChange={setNewItemCountry}
            />
            <label className="echat-field">
              <span className="echat-field__label">Мин. отгрузка</span>
              <input
                className="echat-field__input"
                inputMode="decimal"
                value={newItemMinShipmentQty}
                onChange={(event) => setNewItemMinShipmentQty(event.target.value)}
              />
            </label>
            <label className="echat-field">
              <span className="echat-field__label">Упаковка</span>
              <input
                className="echat-field__input"
                value={newItemPackageSize}
                onChange={(event) => setNewItemPackageSize(event.target.value)}
              />
            </label>
            <ProductTypeCascadePicker
              productTypes={productTypes}
              value={newItemProductTypeId}
              onChange={setNewItemProductTypeId}
              className="market-product-type-cascade market-product-type-cascade--form"
              idPrefix="catalog-create"
            />
          </div>

          {loadingCreateSuggestions ? <p className="echat-muted">Загрузка подсказок…</p> : null}
          {createCandidates.length > 0 ? (
            <div className="market-catalog-edit__suggestions">
              <p className="echat-field__label">Подсказки матчера</p>
              <div className="market-inline-actions">
                {createCandidates.slice(0, 5).map((candidate) => (
                  <button
                    key={candidate.productTypeId}
                    type="button"
                    className="echat-btn echat-btn--ghost echat-btn--sm"
                    onClick={() => setNewItemProductTypeId(candidate.productTypeId)}
                  >
                    {candidate.name} ({Math.round(candidate.score * 100)}%)
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          <div className="market-inline-actions">
            <button
              type="button"
              className="echat-btn echat-btn--primary"
              disabled={creatingItem || !newItemName.trim()}
              onClick={() => void handleCreateItem()}
            >
              {creatingItem ? 'Сохранение…' : 'Создать'}
            </button>
            <button
              type="button"
              className="echat-btn echat-btn--ghost"
              disabled={creatingItem}
              onClick={() => {
                resetCreateForm()
                setShowCreateForm(false)
              }}
            >
              Отмена
            </button>
          </div>
        </article>
      ) : null}

      <div
        className={
          isRestaurantMyPrices
            ? 'market-filters market-filters--my-prices'
            : 'market-filters market-filters--supplier-catalog'
        }
      >
        {isRestaurantMyPrices ? (
          <>
            <div className="market-page__filter-bar">
              <button
                type="button"
                className="market-filters__more-toggle"
                aria-expanded={showMoreFilters}
                aria-controls="my-prices-more-filters"
                onClick={() => setShowMoreFilters((current) => !current)}
              >
                {showMoreFilters ? 'Скрыть фильтры' : 'Ещё фильтры'}
                <svg
                  className={`market-filters__more-chevron${showMoreFilters ? ' market-filters__more-chevron--open' : ''}`}
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  aria-hidden="true"
                >
                  <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>

            <div className="market-filters__row market-filters__row--primary">
              <label className="echat-field market-filters__field market-filters__field--search">
                <span className="echat-field__label">Поиск</span>
                <input
                  className="echat-field__input"
                  value={searchDraft}
                  placeholder="Название или бренд"
                  onChange={(event) => setSearchDraft(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      handleSearch()
                    }
                  }}
                />
              </label>

              <ProductTypeCascadePicker
                productTypes={productTypes}
                value={productTypeFilter}
                onChange={setProductTypeFilter}
                compact
                emptyLabel="Все"
                disabled={unassignedOnly}
                showCascadeLevels={false}
                quickSearchClassName="market-filters__field market-filters__field--quick-search"
                idPrefix="my-prices-filter"
              />

              <div className="market-filters__actions">
                <span className="echat-field__label market-filters__actions-label" aria-hidden="true">
                  &nbsp;
                </span>
                <div className="market-filters__actions-row">
                  <TableColumnSettings prefs={columnPrefs} />
                  <button type="button" className="echat-btn echat-btn--primary" onClick={handleSearch}>
                    Найти
                  </button>
                  <button type="button" className="echat-btn echat-btn--ghost" onClick={handleResetSearch}>
                    Сбросить
                  </button>
                  {selectedIds.length > 0 ? (
                    <>
                      <button
                        type="button"
                        className="echat-btn echat-btn--primary"
                        onClick={handleBulkAddToCart}
                      >
                        В заявку ({selectedIds.length})
                      </button>
                      <button
                        type="button"
                        className="echat-btn echat-btn--ghost"
                        onClick={handleBulkEdit}
                        disabled={selectedIds.length !== 1}
                        title={
                          selectedIds.length === 1
                            ? 'Редактировать выбранную позицию'
                            : 'Выберите одну позицию'
                        }
                      >
                        Изменить
                      </button>
                      <button
                        type="button"
                        className="echat-btn echat-btn--danger"
                        onClick={() => void handleBulkDelete()}
                      >
                        Удалить ({selectedIds.length})
                      </button>
                    </>
                  ) : null}
                </div>
              </div>
            </div>

            <div
              id="my-prices-more-filters"
              className={`market-filters__more${showMoreFilters ? ' is-open' : ''}`}
              hidden={!showMoreFilters}
            >
              <SelectCombobox
                className="market-filters__field market-filters__field--status"
                label="Тип определения"
                value={matchStatus}
                options={[
                  { value: '', label: 'Все' },
                  { value: 'auto', label: matchDeterminationLabel('auto') },
                  { value: 'suggested', label: matchDeterminationLabel('suggested') },
                  { value: 'manual', label: matchDeterminationLabel('manual') },
                  { value: 'unmatched', label: matchDeterminationLabel('unmatched') },
                ]}
                onChange={setMatchStatus}
              />

              <SelectCombobox
                className="market-filters__field"
                label="Поставщик"
                value={privateSupplierId != null ? String(privateSupplierId) : ''}
                options={[
                  { value: '', label: 'Все' },
                  ...privateSuppliers.map((supplier) => ({
                    value: String(supplier.id),
                    label: `${supplier.displayLabel || supplier.legalName}${supplier.inn ? ` · ИНН ${supplier.inn}` : ''}`,
                  })),
                ]}
                onChange={(next) => setPrivateSupplierId(next ? Number(next) : null)}
              />

              <label className="market-filters__checkbox market-filters__checkbox--inline">
                <input
                  type="checkbox"
                  checked={unassignedOnly}
                  onChange={(event) => {
                    setUnassignedOnly(event.target.checked)
                    if (event.target.checked) {
                      setProductTypeFilter(null)
                    }
                  }}
                />
                <span>Только без категории</span>
              </label>

              <ProductTypeCascadePicker
                productTypes={productTypes}
                value={productTypeFilter}
                onChange={setProductTypeFilter}
                compact
                emptyLabel="Не выбран"
                disabled={unassignedOnly}
                showQuickSearch={false}
                className="market-product-type-cascade market-product-type-cascade--levels-only"
                idPrefix="my-prices-filter-more"
              />
            </div>
          </>
        ) : (
          <>
            <div className="market-page__filter-bar">
              <button
                type="button"
                className="market-filters__more-toggle"
                aria-expanded={showMoreFilters}
                aria-controls="supplier-catalog-more-filters"
                onClick={() => setShowMoreFilters((current) => !current)}
              >
                {showMoreFilters ? 'Скрыть фильтры' : 'Ещё фильтры'}
                <svg
                  className={`market-filters__more-chevron${showMoreFilters ? ' market-filters__more-chevron--open' : ''}`}
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  aria-hidden="true"
                >
                  <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>

            <ProductTypeCascadePicker
              productTypes={productTypes}
              value={productTypeFilter}
              onChange={setProductTypeFilter}
              compact
              emptyLabel="Все"
              disabled={unassignedOnly}
              idPrefix="catalog-filter"
            />

            <label className="echat-field market-filters__field market-filters__field--search">
              <span className="echat-field__label">Поиск</span>
              <input
                className="echat-field__input"
                value={searchDraft}
                onChange={(event) => setSearchDraft(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    handleSearch()
                  }
                }}
              />
            </label>

            <div className="market-filters__actions">
              <TableColumnSettings prefs={columnPrefs} />
              <button type="button" className="echat-btn echat-btn--primary" onClick={handleSearch}>
                Найти
              </button>
              <button type="button" className="echat-btn echat-btn--ghost" onClick={handleResetSearch}>
                Сбросить
              </button>
              {selectedIds.length > 0 ? (
                <button
                  type="button"
                  className="echat-btn echat-btn--danger"
                  onClick={() => void handleBulkDelete()}
                >
                  Удалить ({selectedIds.length})
                </button>
              ) : null}
            </div>

            <div
              id="supplier-catalog-more-filters"
              className={`market-filters__more${showMoreFilters ? ' is-open' : ''}`}
              hidden={!showMoreFilters}
            >
              <SelectCombobox
                className="market-filters__field market-filters__field--status"
                label="Тип определения"
                value={matchStatus}
                options={[
                  { value: '', label: 'Все' },
                  { value: 'auto', label: matchDeterminationLabel('auto') },
                  { value: 'suggested', label: matchDeterminationLabel('suggested') },
                  { value: 'manual', label: matchDeterminationLabel('manual') },
                  { value: 'unmatched', label: matchDeterminationLabel('unmatched') },
                ]}
                onChange={setMatchStatus}
              />

              <label className="echat-field market-filters__checkbox market-filters__checkbox--inline">
                <input
                  type="checkbox"
                  checked={unassignedOnly}
                  onChange={(event) => {
                    setUnassignedOnly(event.target.checked)
                    if (event.target.checked) {
                      setProductTypeFilter(null)
                    }
                  }}
                />
                <span>Только без категории</span>
              </label>
            </div>
          </>
        )}
      </div>

      {loading ? <p className="echat-muted">Загрузка…</p> : null}
      {error ? (
        <p className="echat-error" role="alert">
          {error}
        </p>
      ) : null}
      {rematchMessage ? <p className="echat-muted">{rematchMessage}</p> : null}

      {page && page.items.length > 0 ? (
        <>
        {isRestaurantMyPrices ? (
          <>
            <div id="my-prices-table-top" />
            <TableScrollJump direction="down" targetId="my-prices-table-bottom" />
          </>
        ) : null}
        <DataTable
          className="market-table--expandable market-table--cards market-table--configurable"
          wrapClassName="market-table-wrap--cards"
          caption={isRestaurantMyPrices ? 'Мои прайсы' : 'Каталог поставщика'}
        >
            <colgroup>
              {columnPrefs.visibleColumns.map((column) => (
                <col
                  key={column.id}
                  style={
                    columnPrefs.getWidth(column.id) != null
                      ? { width: columnPrefs.getWidth(column.id) }
                      : undefined
                  }
                />
              ))}
            </colgroup>
            <thead>
              <tr>
                {columnPrefs.visibleColumns.map((column) => (
                  <ResizableTh
                    key={column.id}
                    columnId={column.id}
                    label={column.label}
                    width={columnPrefs.getWidth(column.id)}
                    minWidth={columnPrefs.getMinWidth(column.id)}
                    className={
                      column.id === 'select'
                        ? 'market-table__col--check'
                        : column.id === 'actions'
                          ? 'market-table__cell--action'
                          : undefined
                    }
                    onResize={columnPrefs.setWidth}
                  >
                    {column.id === 'select' || column.id === 'actions' ? null : column.label}
                  </ResizableTh>
                ))}
              </tr>
            </thead>
            <tbody>
              {page.items.map((item, index) => {
                const expanded = expandedId === item.id
                const selected = selectedIds.includes(item.id)
                const stripeClass = index % 2 === 0 ? 'market-table__row--odd' : 'market-table__row--even'
                const rowClassName = [
                  'market-catalog-row',
                  stripeClass,
                  expanded ? 'market-table__row--expanded' : null,
                  selected ? 'market-table__row--selected' : null,
                ]
                  .filter(Boolean)
                  .join(' ')
                const freshnessClass = item.freshnessStatus
                  ? `market-catalog-freshness market-catalog-freshness--${item.freshnessStatus.replace('_', '-')}`
                  : 'market-catalog-freshness'
                const hint = freshnessHint(item, !isRestaurantMyPrices)
                return (
                  <Fragment key={item.id}>
                    <tr
                      className={rowClassName || undefined}
                      onClick={() => toggleSelected(item.id)}
                      onDoubleClick={() => {
                        if (isRestaurantMyPrices) {
                          setExpandedId(expanded ? null : item.id)
                        }
                      }}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault()
                          toggleSelected(item.id)
                        }
                      }}
                      tabIndex={0}
                      aria-selected={selected}
                      aria-label={`${item.supplierName}, ${selected ? 'выбрано' : 'не выбрано'}${hint ? `, ${hint}` : ''}`}
                    >
                      {columnPrefs.visibleColumns.map((column) => {
                        switch (column.id) {
                          case 'select':
                            return (
                              <td
                                key={column.id}
                                className="market-table__col--check"
                                onClick={(event) => event.stopPropagation()}
                              >
                                <input
                                  type="checkbox"
                                  aria-label={`Выбрать ${item.supplierName}`}
                                  checked={selected}
                                  onChange={() => toggleSelected(item.id)}
                                />
                              </td>
                            )
                          case 'name':
                            return (
                              <td key={column.id} data-label="Позиция" className="market-table__cell--primary">
                                {item.supplierName}
                              </td>
                            )
                          case 'supplier':
                            return (
                              <td key={column.id} data-label="Поставщик">
                                {item.privateSupplierName ?? '—'}
                              </td>
                            )
                          case 'sku':
                            return (
                              <td key={column.id} data-label="Артикул">
                                {item.externalSku?.trim() || '—'}
                              </td>
                            )
                          case 'price': {
                            if (!isRestaurantMyPrices) {
                              return (
                                <td key={column.id} data-label="Цена" className="market-table__cell--price">
                                  {formatCatalogPrice(item)}
                                </td>
                              )
                            }
                            const deltaPct = item.priceDeltaPct
                            const previous = item.previousPrice
                            const hasChange =
                              previous != null && deltaPct != null && Number(deltaPct) !== 0
                            const unchanged = previous != null && (deltaPct == null || Number(deltaPct) === 0)
                            const deltaAbs =
                              item.price != null && previous != null ? item.price - previous : 0
                            return (
                              <td
                                key={column.id}
                                data-label="Цена"
                                className="market-table__cell--price market-my-prices-price"
                              >
                                <div className="market-my-prices-price__row">
                                  <span className="market-my-prices-price__current">
                                    {formatCatalogPriceRub(item.price)}
                                  </span>
                                  {hasChange ? (
                                    <span className={priceMoveToneClass(deltaAbs)}>
                                      {deltaAbs < 0 ? '↓' : '↑'}{' '}
                                      {formatAnalyticsPct(deltaPct).replace('.', ',')}
                                    </span>
                                  ) : null}
                                  {unchanged ? (
                                    <span className="market-analytics-delta">без изм.</span>
                                  ) : null}
                                </div>
                              </td>
                            )
                          }
                          case 'unit':
                            return (
                              <td key={column.id} data-label="Ед. изм.">
                                {item.unit ? formatUnitLabel(item.unit) : '—'}
                              </td>
                            )
                          case 'package':
                            return (
                              <td key={column.id} data-label="Фасовка">
                                {item.packageSize ?? '—'}
                              </td>
                            )
                          case 'minShipment':
                            return (
                              <td key={column.id} data-label="Мин. отгрузка">
                                {item.minShipmentQty != null ? String(item.minShipmentQty) : '—'}
                              </td>
                            )
                          case 'brand':
                            return (
                              <td key={column.id} data-label="Бренд">
                                {item.brand ?? '—'}
                              </td>
                            )
                          case 'country':
                            return (
                              <td key={column.id} data-label="Страна">
                                {item.country ?? '—'}
                              </td>
                            )
                          case 'category':
                            return (
                              <td key={column.id} data-label="Категория">
                                {formatProductTypeLabel(
                                  item.productTypeId,
                                  item.productTypeName,
                                  item.productTypeActive,
                                  typeNames,
                                )}
                              </td>
                            )
                          case 'updated': {
                            const priceMoved =
                              isRestaurantMyPrices &&
                              item.previousPrice != null &&
                              item.priceDeltaPct != null &&
                              Number(item.priceDeltaPct) !== 0
                            const priceUp =
                              priceMoved &&
                              item.price != null &&
                              item.previousPrice != null &&
                              item.price > item.previousPrice
                            return (
                              <td key={column.id} data-label="Обновление">
                                <div className={freshnessClass}>
                                  <span className="market-catalog-freshness__value">
                                    {formatUpdatedAt(item.updatedAt)}
                                  </span>
                                  {priceMoved ? (
                                    <span
                                      className={
                                        priceUp
                                          ? 'market-catalog-freshness__hint market-catalog-freshness__hint--up'
                                          : 'market-catalog-freshness__hint market-catalog-freshness__hint--down'
                                      }
                                    >
                                      цена изменилась
                                    </span>
                                  ) : hint ? (
                                    <span className="market-catalog-freshness__hint">{hint}</span>
                                  ) : null}
                                </div>
                              </td>
                            )
                          }
                          case 'match':
                            return (
                              <td key={column.id} data-label="Тип определения">
                                {matchDeterminationLabel(item.matchStatus)}
                              </td>
                            )
                          case 'actions':
                            return (
                              <td
                                key={column.id}
                                className="market-table__cell--action"
                                onClick={(event) => event.stopPropagation()}
                              >
                                <div className="market-table__row-actions">
                                  {(item.matchStatus.toLowerCase() === 'suggested'
                                    || item.matchStatus.toLowerCase() === 'auto')
                                  && item.productTypeId != null ? (
                                    <button
                                      type="button"
                                      className="echat-btn echat-btn--ghost market-table__icon-btn market-table__icon-btn--confirm"
                                      aria-label={`Подтвердить категорию для ${item.supplierName}`}
                                      title={item.matchStatus.toLowerCase() === 'auto' ? 'Подтвердить авто' : 'Подтвердить'}
                                      disabled={confirmingId === item.id || loading}
                                      onClick={() => void handleConfirmMatch(item)}
                                    >
                                      <IconCheck />
                                    </button>
                                  ) : null}
                                  <button
                                    type="button"
                                    className="echat-btn echat-btn--ghost market-table__icon-btn market-table__icon-btn--danger"
                                    aria-label={`Удалить ${item.supplierName}`}
                                    title="Удалить"
                                    disabled={deletingId === item.id || loading}
                                    onClick={() => void handleQuickDelete(item)}
                                  >
                                    <IconTrash />
                                  </button>
                                  <button
                                    type="button"
                                    className="echat-btn echat-btn--ghost market-table__icon-btn"
                                    aria-label={expanded ? `Свернуть ${item.supplierName}` : `Изменить ${item.supplierName}`}
                                    title={expanded ? 'Свернуть' : 'Изменить'}
                                    onClick={() => setExpandedId(expanded ? null : item.id)}
                                  >
                                    <IconPencil />
                                  </button>
                                </div>
                              </td>
                            )
                          default:
                            return null
                        }
                      })}
                    </tr>
                    {expanded ? (
                      <tr className="market-table__edit-row">
                        <td colSpan={columnPrefs.visibleCount}>
                          <CatalogItemEditPanel
                            item={item}
                            organizationId={organizationId}
                            productTypes={productTypes}
                            brandOptions={brandOptions}
                            countryOptions={countryOptions}
                            catalogMode={catalogMode}
                            onSaved={handleItemSaved}
                            onDeleted={handleItemDeleted}
                          />
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                )
              })}
            </tbody>
        </DataTable>
        {isRestaurantMyPrices ? (
          <>
            <div id="my-prices-table-bottom" />
            <TableScrollJump direction="up" targetId="my-prices-table-top" />
          </>
        ) : null}
        </>
      ) : null}

      {isRestaurantMyPrices && page && page.items.length > 0 ? (
        <PageScrollJumpButtons />
      ) : null}

      {!loading && page?.items.length === 0 ? (
        <MarketEmptyState
          title={isRestaurantMyPrices ? 'Прайсы не загружены' : 'Каталог пуст'}
          description={
            isRestaurantMyPrices
              ? 'Загрузите прайс поставщика в формате xlsx, xls или csv — позиции появятся здесь и в поиске каталога.'
              : 'Загрузите прайс-лист в формате xlsx, xls или csv — позиции появятся здесь после импорта.'
          }
          actions={
            isRestaurantMyPrices
              ? [{ label: 'Импорт прайса', onClick: () => navigate(importPath) }]
              : [
                  { label: 'Импорт прайса', onClick: () => navigate(importPath) },
                  { label: 'Добавить позицию', onClick: () => setShowCreateForm(true), variant: 'ghost' },
                ]
          }
        />
      ) : null}

      {page && page.totalPages > 1 ? (
        <div className="market-pagination">
          <button
            type="button"
            className="echat-btn echat-btn--ghost"
            disabled={pageIndex <= 0}
            onClick={() => void load(pageIndex - 1, matchStatus, productTypeFilter, unassignedOnly, search, privateSupplierId)}
          >
            ←
          </button>
          <span>
            Страница {page.page + 1} из {page.totalPages}
          </span>
          <button
            type="button"
            className="echat-btn echat-btn--ghost"
            disabled={pageIndex >= page.totalPages - 1}
            onClick={() => void load(pageIndex + 1, matchStatus, productTypeFilter, unassignedOnly, search, privateSupplierId)}
          >
            →
          </button>
        </div>
      ) : null}
    </section>
  )
}
